import React, { useEffect, useState } from "react";
import { fetchCosts, addCost, updateCost, deleteCost } from "../api/api";

function AdminCostsPage() {
  const [costs, setCosts] = useState([]);
  const [form, setForm] = useState({ name: "", amount: "" });
  const [editingId, setEditingId] = useState(null);
  const [toastMessage, setToastMessage] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null });

  const showToast = (message) => {
    setToastMessage(message);
    setTimeout(() => setToastMessage(""), 2500);
  };

  useEffect(() => {
    loadCosts();
  }, []);

  const loadCosts = async () => {
    const data = await fetchCosts();
    setCosts(data);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.amount) {
      showToast("⚠️ يرجى إدخال اسم وتكلفة.");
      return;
    }

    try {
      if (editingId) {
        await updateCost(editingId, form);
        showToast("✅ تم تحديث التكلفة بنجاح");
      } else {
        await addCost(form);
        showToast("✅ تم إضافة التكلفة بنجاح");
      }

      setForm({ name: "", amount: "" });
      setEditingId(null);
      loadCosts();
    } catch {
      showToast("❌ حدث خطأ أثناء الحفظ");
    }
  };

  const handleEdit = (cost) => {
    setForm({ name: cost.name, amount: cost.amount });
    setEditingId(cost.id);
  };

  const confirmDelete = async () => {
    try {
      await deleteCost(deleteConfirm.id);
      showToast("🗑️ تم حذف التكلفة");
      loadCosts();
      setDeleteConfirm({ isOpen: false, id: null });
    } catch {
      showToast("❌ فشل في حذف التكلفة");
    }
  };

  // حساب مجموع التكاليف
  const totalAmount = costs.reduce((sum, cost) => sum + Number(cost.amount), 0).toFixed(2);

  return (
    <div style={styles.container}>
      {toastMessage && <div style={styles.toast}>{toastMessage}</div>}

      <h2 style={styles.pageTitle}>💰 إدارة التكاليف</h2>

      <div style={styles.summaryCards}>
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>📦 عدد التكاليف</h3>
          <p style={styles.cardValue}>{costs.length}</p>
        </div>
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>💵 مجموع التكاليف</h3>
          <p style={{ ...styles.cardValue, color: "#e76a3c" }}>{totalAmount} ₪</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} style={styles.form}>
        <input
          type="text"
          placeholder="اسم التكلفة"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          style={styles.input}
        />
        <input
          type="number"
          placeholder="القيمة (₪)"
          value={form.amount}
          onChange={(e) => setForm({ ...form, amount: e.target.value })}
          style={styles.input}
        />
        <button type="submit" style={styles.submitBtn}>
          {editingId ? "✏️ تحديث" : "➕ إضافة"}
        </button>
      </form>

      <div style={{ overflowX: "auto" }}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th>اسم التكلفة</th>
              <th>المبلغ (₪)</th>
              <th>إجراءات</th>
            </tr>
          </thead>
          <tbody>
            {costs.map((cost) => (
              <tr key={cost.id}>
                <td>{cost.name}</td>
                <td>{cost.amount}</td>
                <td>
                  <button style={styles.editBtn} onClick={() => handleEdit(cost)}>
                    ✏️ تعديل
                  </button>
                  <button style={styles.deleteBtn} onClick={() => setDeleteConfirm({ isOpen: true, id: cost.id })}>
                    🗑️ حذف
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ✅ نافذة تأكيد الحذف */}
      {deleteConfirm.isOpen && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <h3>⚠️ هل تريد حذف هذه التكلفة؟</h3>
            <div style={styles.modalActions}>
              <button style={styles.confirmBtn} onClick={confirmDelete}>نعم، احذف</button>
              <button style={styles.cancelBtn} onClick={() => setDeleteConfirm({ isOpen: false, id: null })}>
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* 🎨 Styles */
const styles = {
  container: {
    direction: "rtl",
    fontFamily: "'Cairo', sans-serif",
    padding: "25px",
    backgroundColor: "#fffaf3",
    minHeight: "100vh",
  },
  pageTitle: {
    fontSize: "24px",
    fontWeight: "800",
    textAlign: "center",
    marginBottom: "25px",
    color: "#3e2e26",
  },
  summaryCards: {
    display: "flex",
    justifyContent: "center",
    flexWrap: "wrap",
    gap: "20px",
    marginBottom: "30px",
  },
  card: {
    backgroundColor: "#fff",
    borderRadius: "12px",
    padding: "18px 24px",
    minWidth: "140px",
    width: "100%",
    maxWidth: "220px",
    boxShadow: "0 4px 10px rgba(0,0,0,0.06)",
    textAlign: "center",
    border: "1px solid #f2e6d9",
  },
  cardTitle: {
    fontSize: "15px",
    fontWeight: "600",
    color: "#6e584d",
  },
  cardValue: {
    fontSize: "22px",
    fontWeight: "700",
    color: "#3e2e26",
    marginTop: "8px",
  },
  form: {
    display: "flex",
    gap: "10px",
    marginBottom: "25px",
    justifyContent: "center",
    flexWrap: "wrap",
  },
  input: {
    padding: "8px 10px",
    fontSize: "15px",
    borderRadius: "6px",
    border: "1px solid #dec6b3",
    width: "100%",
    maxWidth: "220px",
    backgroundColor: "white",
  },
  submitBtn: {
    padding: "8px 20px",
    backgroundColor: "#ff7b3b",
    color: "#fff",
    borderRadius: "6px",
    cursor: "pointer",
    border: "none",
    fontWeight: "700",
    fontSize: "15px",
    boxShadow: "0 4px 12px rgba(255,110,60,0.2)",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    backgroundColor: "#fff",
    boxShadow: "0 4px 10px rgba(0,0,0,0.05)",
    borderRadius: "12px",
    overflow: "hidden",
  },
  editBtn: {
    marginRight: "8px",
    padding: "6px 10px",
    backgroundColor: "#ffc107",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
  },
  deleteBtn: {
    padding: "6px 10px",
    backgroundColor: "#e63946",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
  },
  toast: {
    position: "fixed",
    top: "15px",
    left: "50%",
    transform: "translateX(-50%)",
    backgroundColor: "#333",
    color: "#fff",
    padding: "10px 16px",
    borderRadius: "6px",
    fontSize: "14px",
    zIndex: 999,
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: "#fff",
    padding: "20px",
    borderRadius: "10px",
    textAlign: "center",
    boxShadow: "0 5px 15px rgba(0,0,0,0.1)",
    width: "90%",
    maxWidth: "350px",
  },
  modalActions: {
    display: "flex",
    justifyContent: "center",
    gap: "10px",
    marginTop: "15px",
  },
  confirmBtn: {
    backgroundColor: "#e63946",
    color: "#fff",
    border: "none",
    padding: "8px 14px",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "600",
  },
  cancelBtn: {
    backgroundColor: "#6c757d",
    color: "#fff",
    border: "none",
    padding: "8px 14px",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "600",
  },
};

export default AdminCostsPage;
